import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { productAPI, orderAPI, dealerAPI, analyticsAPI, exportAPI, transporterAPI, crmAPI, stockAPI, productionAPI, freightAPI, getFileUrl } from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LayoutDashboard, Package, ShoppingBag, Users, LogOut, Plus, Edit, Trash2, CheckCircle, Truck, Loader2, FileText, BarChart3, PackageCheck, DollarSign, Download, IndianRupee, Upload, ImageIcon, ArrowUpDown, Eye, CalendarClock, Pencil, ClipboardList, Undo2, Menu, X, UserRound, MessageCircle, ArrowLeft, Warehouse, Minus, Factory, FlaskConical } from 'lucide-react';
import { InvoiceView } from './InvoiceView';

import { MainMenu } from './admin/MainMenu';
import { DashboardTab } from './admin/DashboardTab';
import { ProductsTab } from './admin/ProductsTab';
import { OrdersTab } from './admin/OrdersTab';
import { NewOrderTab } from './admin/NewOrderTab';
import { DealersTab } from './admin/DealersTab';
import { DealerCRMTab } from './admin/DealerCRMTab';
import { ReportsTab } from './admin/ReportsTab';
import { ProductSummaryTab } from './admin/ProductSummaryTab';
import { TransportTab } from './admin/TransportTab';
import { StockTab } from './admin/StockTab';
import { ProductionTab } from './admin/ProductionTab';
import { PlanningTab } from './admin/PlanningTab';
import { UsersTab } from './admin/UsersTab';
import { ProductCostingTab } from './admin/ProductCostingTab';

// Single source of truth for sidebar/burger nav items (kept in sync with MainMenu tiles)
const NAV_ITEMS = [
  { id: 'menu', label: 'Menu', icon: LayoutDashboard },
  { id: 'new-order', label: 'New Order', icon: Plus },
  { id: 'orders', label: 'Orders', icon: ShoppingBag },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'product-summary', label: 'Product Summary', icon: ClipboardList },
  { id: 'products', label: 'Products', icon: Package },
  { id: 'dealers', label: 'Dealers', icon: Users },
  { id: 'dealer-crm', label: 'Dealer CRM', icon: IndianRupee },
  { id: 'reports', label: 'Reports', icon: BarChart3 },
  { id: 'transport', label: 'Transport', icon: Truck },
  { id: 'stock', label: 'Stock', icon: Warehouse },
  { id: 'planning', label: 'Planning', icon: ClipboardList },
  { id: 'production', label: 'Production', icon: Factory },
  { id: 'users', label: 'Users', icon: UserRound },
  { id: 'costing', label: 'Product Costing', icon: FlaskConical },
];

export const AdminDashboard = () => {
  const { user, logout, canNav, stopImpersonating } = useAuth();
  // Filter NAV items by the user's permissions_effective so non-admin staff
  // only see the tabs they're allowed to enter. Admins see everything.
  const visibleNavItems = NAV_ITEMS.filter((it) => (it.id === 'costing' ? user?.role === 'admin' : canNav(it.id)));
  // When the current session is an impersonation, surface a sticky banner
  // that lets the original admin restore their own session in one click.
  const impersonatedBy = user?._impersonated_by;
  const [activeTab, setActiveTabRaw] = useState('dashboard');
  const [tabHistory, setTabHistory] = useState([]);  // stack of previously-visited tabs
  // Per-tab refresh nonce. Bumped on re-click of the active tab (or 'R' shortcut)
  // to force only THAT tab to remount. Other tabs stay mounted with their state intact.
  const [tabNonces, setTabNonces] = useState({ dashboard: 0 });
  // Tabs that have ever been activated. Mounted tabs stay alive (with display:none) so
  // user-entered filters, expansions, scroll position etc. are preserved across navigations
  // until a full app refresh.
  const [mountedTabs, setMountedTabs] = useState(new Set(['dashboard']));
  const tabNonce = tabNonces[activeTab] || 0;
  const setActiveTab = (next) => {
    setActiveTabRaw((prev) => {
      if (prev !== next) {
        setTabHistory((h) => (h.length && h[h.length - 1] === prev) ? h : [...h, prev]);
        setMountedTabs((s) => s.has(next) ? s : new Set([...s, next]));
      } else {
        // Re-click of the same tab → force refresh by bumping THIS tab's nonce
        setTabNonces((n) => ({ ...n, [next]: (n[next] || 0) + 1 }));
        const labelMap = Object.fromEntries(NAV_ITEMS.map((t) => [t.id, t.label]));
        toast.success(`${labelMap[next] || 'Tab'} refreshed`, { duration: 1500 });
      }
      return next;
    });
  };
  const goBack = () => {
    setTabHistory((h) => {
      if (h.length === 0) { setActiveTabRaw('menu'); return []; }
      const last = h[h.length - 1];
      setActiveTabRaw(last);
      setMountedTabs((s) => s.has(last) ? s : new Set([...s, last]));
      return h.slice(0, -1);
    });
  };
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [transporters, setTransporters] = useState([]);
  const [crmOutstandingByDealerId, setCrmOutstandingByDealerId] = useState({});
  const [freightByOrderId, setFreightByOrderId] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [dealerDialogOpen, setDealerDialogOpen] = useState(false);
  const [dealerReports, setDealerReports] = useState(null);
  const [drillFilter, setDrillFilter] = useState(null); // { type: 'dealer'|'status'|'order', value: string }
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [ordersVisitKey, setOrdersVisitKey] = useState(0); // Bumping this forces OrdersTab to remount (fresh state)
  // Cross-tab hand-off: PlanningTab → OrdersTab "Edit Order" jump.
  // When set, OrdersTab auto-opens the EditOrderDialog for this order id.
  const [pendingEditOrderId, setPendingEditOrderId] = useState(null);
  // When the Planning popup hands off to the OrdersTab edit flow, we stash
  // the drill context here so that closing the edit dialog (cancel or save)
  // can navigate back to PlanningTab AND re-open the same popup the user
  // started from (user spec: "always de-escalate one step only").
  const [planningReturnContext, setPlanningReturnContext] = useState(null);
  // Restoration is two-phase: we save the context on Edit-click but only
  // arm the actual popup restoration when the edit dialog has closed.
  // Without this gate the PlanningTab restore-effect fires the moment
  // `planningReturnContext` becomes non-null and re-opens the popup BEFORE
  // we even navigate away.
  const [planningRestoreArmed, setPlanningRestoreArmed] = useState(false);
  const triggerEditOrderFromPlanning = (orderId, drillContext) => {
    setPendingEditOrderId(orderId);
    setPlanningReturnContext(drillContext || null);
    setPlanningRestoreArmed(false); // safety — we're going OUT, not returning
    setActiveTab('orders');
  };
  const handlePendingEditDialogClosed = () => {
    if (!planningReturnContext) return;
    setActiveTab('planning');
    setPlanningRestoreArmed(true); // arm the PlanningTab restore-effect
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // If the freshly-loaded user does NOT have permission to view the current
  // tab (most commonly: they don't have `dashboard` view but we default to
  // 'dashboard' on first mount), bounce them onto the Main Menu welcome
  // screen so they never see a tab they can't access. The Main Menu itself
  // already filters its tiles via `canNav`.
  useEffect(() => {
    if (!user) return;
    if (activeTab !== 'menu' && !canNav(activeTab)) {
      setActiveTabRaw('menu');
      setMountedTabs((s) => { const n = new Set(s); n.delete(activeTab); return n; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Auto-refresh dashboard data when the user returns to the tab.
  // Many users keep the app open in a background tab — this ensures they always see fresh
  // orders/stock/CRM the moment they switch back, without having to press F5 / R.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchAllData();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Tabs that should refresh their internal data on tab re-entry.
  //
  // - For tabs with `key="<tab>-persistent"`: refreshKey bumps and the tab's
  //   internal effect calls load() silently — preserves scroll/expanded state.
  // - For 'stock' (18/Jun/2026): the key embeds tabNonces.stock so the WHOLE
  //   component remounts on every entry → no stale UI state at all (user
  //   reported lingering dismissed-alert / expand / fix-overlay memory).
  const PRESERVE_STATE_TABS = React.useMemo(() => new Set(['stock', 'planning', 'production', 'dealer-crm', 'product-summary']), []);
  useEffect(() => {
    if (PRESERVE_STATE_TABS.has(activeTab)) {
      setTabNonces((n) => ({ ...n, [activeTab]: (n[activeTab] || 0) + 1 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tabNonce]);

  // Keyboard shortcut: press `R` (when not typing in an input/textarea) to refresh the active tab
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'r' && e.key !== 'R') return;
      if (e.ctrlKey || e.metaKey || e.altKey) return; // let browser handle Ctrl/Cmd+R
      const t = e.target;
      const tag = (t?.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable) return;
      // Ignore when a Radix dropdown/select/combobox/menu/dialog is open — its
      // type-ahead (e.g. pressing "R" to jump to a product) lands on a
      // role="listbox"/combobox element, not an <input>, and must not trigger
      // the tab refresh. Covers open Select/Popover/DropdownMenu/Dialog.
      if (
        t?.closest?.('[role="combobox"], [role="listbox"], [role="menu"], [role="dialog"], [data-radix-popper-content-wrapper]') ||
        document.querySelector('[data-radix-popper-content-wrapper], [role="dialog"][data-state="open"], [role="listbox"]')
      ) return;
      e.preventDefault();
      setTabNonces((n) => ({ ...n, [activeTab]: (n[activeTab] || 0) + 1 }));
      const labelMap = Object.fromEntries(NAV_ITEMS.map((it) => [it.id, it.label]));
      toast.success(`${labelMap[activeTab] || 'Tab'} refreshed`, { duration: 1500 });
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeTab]);

  // iOS-style edge swipe-back gesture (mobile only):
  // - touchstart must begin within ~25px of the LEFT edge
  // - touchend with horizontal travel > 80px and minimal vertical drift
  // - if a Radix dialog is currently open, dispatch Escape to close it (top-most first);
  //   otherwise call goBack() to return to the previous tab. State is preserved because
  //   tabs stay mounted (display:none) once they've been visited.
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    let active = false;
    const onTouchStart = (e) => {
      const t = e.touches && e.touches[0];
      if (!t) return;
      if (t.clientX <= 25) {
        startX = t.clientX;
        startY = t.clientY;
        active = true;
      } else {
        active = false;
      }
    };
    const onTouchEnd = (e) => {
      if (!active) return;
      active = false;
      const t = (e.changedTouches && e.changedTouches[0]);
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      if (dx > 80 && dy < 60) {
        const openDialog = document.querySelector('[role="dialog"][data-state="open"]');
        if (openDialog) {
          // Radix Dialog listens for Escape — let it close the topmost dialog
          document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
        } else {
          goBack();
        }
      }
    };
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aggregate freight_lines into a per-order map so the Orders table can show
  // the freight amount payable by the dealer (sum of `by_dealer`) as a green
  // figure, with the underlying lines available for the click-through popup.
  const buildFreightMap = (lines) => {
    const map = {};
    (lines || []).forEach((ln) => {
      const oid = ln.order_id;
      if (!oid) return;
      if (!map[oid]) map[oid] = { by_dealer_total: 0, by_us_total: 0, total_freight: 0, lines: [] };
      map[oid].by_dealer_total += Number(ln.by_dealer || 0);
      map[oid].by_us_total += Number(ln.by_us || 0);
      map[oid].total_freight += Number(ln.total_freight || 0);
      map[oid].lines.push(ln);
    });
    return map;
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, productsRes, ordersRes, dealersRes, reportsRes, transportersRes, crmRes, freightRes, freightEstRes] = await Promise.all([
        analyticsAPI.getDashboard(),
        productAPI.getAll(),
        orderAPI.getAll(),
        dealerAPI.getAll(),
        analyticsAPI.getDealerReports(),
        transporterAPI.list(),
        crmAPI.getDealers().catch(() => ({ data: [] })),
        freightAPI.listLines().catch(() => ({ data: [] })),
        freightAPI.orderEstimates().catch(() => ({ data: {} })),
      ]);
      setAnalytics(analyticsRes.data);
      setProducts(productsRes.data);
      setOrders(ordersRes.data);
      setDealers(dealersRes.data);
      setDealerReports(reportsRes.data);
      setTransporters(transportersRes.data);
      const osMap = {};
      (crmRes.data || []).forEach((c) => {
        if (c?.id) {
          osMap[c.id] = {
            outstanding_total: Number(c.outstanding_total || 0),
            oldest_od_days: Number(c.oldest_od_days || 0),
            is_overdue: !!c.is_overdue,
          };
        }
      });
      setCrmOutstandingByDealerId(osMap);
      setFreightByOrderId({ ...(freightEstRes.data || {}), ...buildFreightMap(freightRes.data || []) });
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Per-tab refetchers — used after individual mutations so we don't refetch
  // all 7 endpoints every time a user confirms/edits/dispatches an order.
  // `fetchAllData` stays reserved for the initial mount + visibility-change refresh.
  const fetchOrders = async () => {
    try {
      const res = await orderAPI.getAll();
      applyOrdersFromServer(res.data);
    } catch (e) { toast.error('Failed to reload orders'); }
    // Keep freight-per-order figures fresh after dispatch/edit flows (actual
    // lines for dispatched orders + estimates for the rest).
    Promise.all([
      freightAPI.listLines().catch(() => ({ data: [] })),
      freightAPI.orderEstimates().catch(() => ({ data: {} })),
    ]).then(([linesRes, estRes]) => {
      setFreightByOrderId({ ...(estRes.data || {}), ...buildFreightMap(linesRes.data || []) });
    }).catch(() => {});
  };
  // Optimistic patch — splice a single updated order into the local list
  // INSTANTLY (no network round-trip for the UI). Used after edit/toggleHold/
  // dispatch flows so the operator sees their change reflected immediately.
  // Skips the full /orders refetch lag the user reported on 25/Jun/2026.
  // If the patched order doesn't exist (e.g. new order), it's prepended AND
  // its id is stashed in `pendingOptimisticIdsRef` so that a follow-up
  // fetchOrders() response arriving BEFORE the write is visible to that read
  // (rare MongoDB read-your-write hiccup or two concurrent GETs racing) does
  // NOT wipe the optimistic prepend — a fix for the 18/Feb/2026 "newly saved
  // order disappears for a few seconds" bug.
  const pendingOptimisticIdsRef = React.useRef(new Set());
  // Map<id, ISO updated_at> — last optimistic update we applied for that
  // order. Used by applyOrdersFromServer to reject a slightly-stale server
  // row that would otherwise flash-revert (e.g. Hold ON/OFF race reported
  // 04/Aug/2026). We keep the entry for ~15s then evict; well past any
  // read-your-write hiccup window.
  const optimisticUpdatedAtRef = React.useRef(new Map());
  // Just-created flash: order ids that were prepended in the last ~2.5s.
  // OrdersTab reads this and applies a soft-green fade-out animation to the
  // matching <tr> so the operator sees exactly which row is theirs after Save.
  const [justCreatedOrderIds, setJustCreatedOrderIds] = useState(() => new Set());
  const patchOrder = React.useCallback((updated) => {
    if (!updated || !updated.id) return;
    // Stash the optimistic updated_at (fallback to now) so we can compare
    // against server rows arriving after this patch.
    optimisticUpdatedAtRef.current.set(updated.id, updated.updated_at || new Date().toISOString());
    setTimeout(() => { optimisticUpdatedAtRef.current.delete(updated.id); }, 15000);
    setOrders(prev => {
      const idx = prev.findIndex(o => o.id === updated.id);
      if (idx === -1) {
        pendingOptimisticIdsRef.current.add(updated.id);
        // Auto-expire after 30s so the ref can't leak if the server never
        // catches up (e.g., write silently rolled back).
        setTimeout(() => { pendingOptimisticIdsRef.current.delete(updated.id); }, 30000);
        // Mark this row for the "just created" flash animation (2.4s CSS
        // animation + a small safety buffer before removing the class).
        setJustCreatedOrderIds(s => { const n = new Set(s); n.add(updated.id); return n; });
        setTimeout(() => {
          setJustCreatedOrderIds(s => {
            if (!s.has(updated.id)) return s;
            const n = new Set(s); n.delete(updated.id); return n;
          });
        }, 2600);
        return [updated, ...prev];
      }
      pendingOptimisticIdsRef.current.delete(updated.id);
      const next = [...prev];
      next[idx] = updated;
      return next;
    });
    // Refresh this order's freight figure immediately (new order estimate or
    // updated actual lines) so the Orders "Dealer Freight" column fills in at
    // once instead of after the slower bulk recompute.
    freightAPI.orderEstimate(updated.id)
      .then((r) => setFreightByOrderId(prev => ({ ...prev, [updated.id]: r.data })))
      .catch(() => {});
  }, []);
  // Merge a fresh /orders response into local state while PRESERVING any
  // optimistically-prepended orders that the server hasn't yet reflected.
  // Also clears pending ids once the server response contains them.
  //
  // Additionally, if we recently patched an existing order optimistically
  // and the server row we just fetched is OLDER than that patch (per
  // updated_at), keep the local (newer) version. This prevents flash-revert
  // bugs on Hold toggle and similar quick round-trips.
  const applyOrdersFromServer = React.useCallback((serverOrders) => {
    setOrders(prev => {
      const serverIds = new Set((serverOrders || []).map(o => o.id));
      const pendingIds = pendingOptimisticIdsRef.current;
      const optiMap = optimisticUpdatedAtRef.current;
      // Clear pending ids the server has now caught up on.
      Array.from(pendingIds).forEach(id => { if (serverIds.has(id)) pendingIds.delete(id); });
      const stillPending = prev.filter(o => pendingIds.has(o.id) && !serverIds.has(o.id));
      // Build id → local for quick lookup of optimistic rows.
      const localById = new Map(prev.map(o => [o.id, o]));
      const merged = (serverOrders || []).map((so) => {
        const optiTs = optiMap.get(so.id);
        if (!optiTs) return so;
        const local = localById.get(so.id);
        if (!local) return so;
        // Prefer local when server's updated_at is missing or older than
        // our optimistic timestamp — the server hasn't yet reflected the
        // just-completed write.
        const serverTs = so.updated_at || '';
        if (serverTs && serverTs >= optiTs) {
          // Server is caught up; drop the optimistic guard.
          optiMap.delete(so.id);
          return so;
        }
        return local;
      });
      return [...stillPending, ...merged];
    });
  }, []);
  const removeOrder = React.useCallback((orderId) => {
    if (!orderId) return;
    setOrders(prev => prev.filter(o => o.id !== orderId));
  }, []);
  const fetchProducts = async () => {
    try {
      const res = await productAPI.getAll();
      setProducts(res.data);
    } catch (e) { toast.error('Failed to reload products'); }
  };
  const fetchDealers = async () => {
    try {
      const [d, crm] = await Promise.all([dealerAPI.getAll(), crmAPI.getDealers().catch(() => ({ data: [] }))]);
      setDealers(d.data);
      const osMap = {};
      (crm.data || []).forEach((c) => {
        if (c?.id) osMap[c.id] = {
          outstanding_total: Number(c.outstanding_total || 0),
          oldest_od_days: Number(c.oldest_od_days || 0),
          is_overdue: !!c.is_overdue,
        };
      });
      setCrmOutstandingByDealerId(osMap);
    } catch (e) { toast.error('Failed to reload dealers'); }
  };
  const fetchTransporters = async () => {
    try {
      const res = await transporterAPI.list();
      setTransporters(res.data);
    } catch (e) { toast.error('Failed to reload transporters'); }
  };
  const fetchReports = async () => {
    try {
      const res = await analyticsAPI.getDealerReports();
      setDealerReports(res.data);
    } catch (e) { toast.error('Failed to reload reports'); }
  };
  const fetchAnalytics = async () => {
    try {
      const res = await analyticsAPI.getDashboard();
      setAnalytics(res.data);
    } catch (e) { toast.error('Failed to reload analytics'); }
  };
  // Stock and production don't pull data into AdminDashboard state — those
  // tabs own their own fetches — but provide a hook so a mutation in Orders
  // can prompt Stock to re-render through the existing tab-nonce.
  const fetchStock = async () => fetchOrders();

  const fetchData = async () => {
    try {
      if (activeTab === 'dashboard') {
        const res = await analyticsAPI.getDashboard();
        setAnalytics(res.data);
      } else if (activeTab === 'products') {
        const res = await productAPI.getAll();
        setProducts(res.data);
      } else if (activeTab === 'orders') {
        const res = await orderAPI.getAll();
        applyOrdersFromServer(res.data);
      } else if (activeTab === 'dealers') {
        const res = await dealerAPI.getAll();
        setDealers(res.data);
      } else if (activeTab === 'reports') {
        const res = await analyticsAPI.getDealerReports();
        setDealerReports(res.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const getStatusBadge = (status) => {
    // Both `dispatched` and `delivered` are shown together when the "Dispatched"
    // filter is active. We keep them visually distinct so the operator can tell
    // at a glance which orders are still out (DISPATCHED, rust) vs which have
    // landed at the dealer (DELIVERED, forest green).
    const styles = {
      pending: { bg: '#E89D4F', text: 'white' },
      confirmed: { bg: '#2B5339', text: 'white' },
      dispatched: { bg: '#C05C35', text: 'white' },
      partial_dispatched: { bg: '#C05C35', text: 'white' },
      delivered: { bg: '#2B5339', text: 'white' },
    };
    const style = styles[status] || styles.pending;
    return (
      <Badge style={{ background: style.bg, color: style.text }} data-testid={`status-${status}`}>
        {(status || 'pending').toUpperCase()}
      </Badge>
    );
  };

  if (loading && activeTab === 'dashboard') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#2B5339' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#FBF9F6' }}>
      {/* IMPERSONATION BANNER — sticks above everything when an admin has used
          "View as user" from the Users tab. Click "Return to admin" to restore
          the original session token without re-logging-in. */}
      {impersonatedBy && (
        <div className="sticky top-0 z-40 flex items-center justify-between gap-3 px-4 py-2 text-sm font-medium" style={{ background: '#FFE7A6', borderBottom: '2px solid #E89D4F', color: '#1E2420' }} data-testid="impersonation-banner">
          <span>
            <b>Viewing as {user?.name || ''}</b>
            <span className="opacity-70"> · </span>
            <span style={{ color: '#5C6B61' }}>(impersonated by {impersonatedBy.name})</span>
          </span>
          <button
            onClick={() => { if (stopImpersonating()) { window.location.reload(); } }}
            className="text-xs px-3 py-1.5 rounded font-bold"
            style={{ background: '#2B5339', color: 'white' }}
            data-testid="impersonation-stop"
            title="Restore your admin session"
          >Return to admin</button>
        </div>
      )}
      {/* Mobile Top Bar with stacked-on-open right menu */}
      <div className="md:hidden sticky top-0 z-30 border-b" style={{ background: 'white', borderColor: '#E2DFD8' }}>
        <div className="flex items-center gap-2 px-4 py-3">
          <img src="/logo.jpg" alt="Vikas Industries" className="w-10 h-10 rounded object-contain flex-shrink-0" />
          <div className="min-w-0">
            <h1 className="text-base font-bold truncate" style={{ color: '#2B5339' }}>Vikas Industries</h1>
            <p className="text-[11px] truncate" style={{ color: '#5C6B61' }}>{user?.name}</p>
          </div>
          <button
            onClick={() => setMobileNavOpen((v) => !v)}
            className="p-2 rounded hover:bg-[#FBF9F6] transition-colors flex-shrink-0"
            style={{ color: '#2B5339' }}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {mobileNavOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileNavOpen && (
          <nav className="px-3 pb-3 flex flex-col items-stretch gap-1.5" data-testid="mobile-nav-stack">
            <div className="flex flex-col items-end gap-1.5 ml-auto w-56 max-w-full">
              {visibleNavItems.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    // Match MainMenu behaviour: entering Orders from the burger
                    // nav must reset filters/sort/search to defaults, not
                    // reuse a lingering drill from Dashboard/Freight.
                    if (tab.id === 'orders') {
                      setDrillFilter(null);
                      setOrdersVisitKey((k) => k + 1);
                    }
                    setActiveTab(tab.id);
                    setMobileNavOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 rounded transition-colors text-sm ${activeTab === tab.id ? 'bg-[#2B5339] text-white' : 'hover:bg-[#FBF9F6]'}`}
                  style={activeTab !== tab.id ? { color: '#5C6B61' } : {}}
                  data-testid={`${tab.id}-tab`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
              <button
                onClick={logout}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded hover:bg-red-50 transition-colors text-sm"
                style={{ color: '#C05C35' }}
                data-testid="admin-logout-btn-mobile"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </nav>
        )}
      </div>

      {/* Tap-away backdrop for mobile nav */}
      {mobileNavOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/30 z-20"
          onClick={() => setMobileNavOpen(false)}
          data-testid="mobile-nav-backdrop"
          aria-hidden="true"
        />
      )}

      <div className="flex">
        {/* Desktop Sidebar (hidden on mobile) */}
        <div
          className="hidden md:block md:static md:min-h-screen w-64 border-r overflow-y-auto"
          style={{ background: 'white', borderColor: '#E2DFD8' }}
          data-testid="admin-sidebar"
        >
          <div className="p-6 border-b" style={{ borderColor: '#E2DFD8' }}>
            <img src="/logo.jpg" alt="Vikas Industries" className="w-48 max-w-full rounded object-contain mb-2" />
            <h1 className="text-xl font-bold" style={{ color: '#2B5339' }}>Vikas Industries</h1>
            <p className="text-sm truncate" style={{ color: '#5C6B61' }}>{user?.name}</p>
          </div>
          <nav className="p-4">
            {visibleNavItems.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  // Same reset as MainMenu/burger — Orders should re-enter with
                  // date filters NIL, status = All, sort = Order Date desc.
                  if (tab.id === 'orders') {
                    setDrillFilter(null);
                    setOrdersVisitKey((k) => k + 1);
                  }
                  setActiveTab(tab.id);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded mb-2 transition-colors ${activeTab === tab.id ? 'bg-[#2B5339] text-white' : 'hover:bg-[#FBF9F6]'}`}
                style={activeTab !== tab.id ? { color: '#5C6B61' } : {}}
                data-testid={`${tab.id}-tab-desktop`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded hover:bg-red-50 transition-colors"
              style={{ color: '#C05C35' }}
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Logout
            </button>
            <div className="mt-3 px-3 py-1.5 text-[10px] text-center rounded" style={{ color: '#5C6B61', background: '#FBF9F6', border: '1px dashed #E2DFD8' }} data-testid="kbd-hint">
              Tip: press <kbd className="px-1 rounded font-mono" style={{ background: 'white', border: '1px solid #E2DFD8' }}>R</kbd> to refresh this tab
            </div>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0 p-4 md:p-8">
          {activeTab !== 'menu' && activeTab !== 'dashboard' && (() => {
            const prev = tabHistory.length ? tabHistory[tabHistory.length - 1] : null;
            const labelMap = Object.fromEntries(NAV_ITEMS.map((t) => [t.id, t.label]));
            const prevLabel = prev ? (labelMap[prev] || prev) : 'Main Menu';
            return (
              <button
                onClick={goBack}
                className="inline-flex items-center gap-2 mb-4 text-sm font-medium rounded-md px-3 py-1.5 transition-colors hover:bg-[#FBF9F6]"
                style={{ color: '#2B5339', border: '1px solid #E2DFD8', background: 'white' }}
                data-testid="back-to-menu-btn"
                title={`Back to ${prevLabel}`}
              >
                <ArrowLeft className="w-4 h-4" /> Back to {prevLabel}
              </button>
            );
          })()}
          {activeTab === 'menu' && (
            <MainMenu onNavigate={(id) => {
              // When entering Orders from the main menu, force a fresh remount so state
              // (dealer search, schedule date filter, etc.) resets to defaults.
              if (id === 'orders') {
                setDrillFilter(null);
                setOrdersVisitKey((k) => k + 1);
              }
              setActiveTab(id);
            }} />
          )}
          {/* Tabs stay mounted (display:none) once visited so user-entered filters,
              expansions, scroll, etc. are preserved across navigations. The per-tab
              nonce key forces a fresh remount only when that specific tab is refreshed
              via re-click or the 'R' shortcut. */}
          {mountedTabs.has('dashboard') && (
            <div style={{ display: activeTab === 'dashboard' ? 'block' : 'none' }}>
              <DashboardTab
                key={`dashboard-${tabNonces.dashboard || 0}`}
                orders={orders}
                onNewOrder={() => setActiveTab('new-order')}
                onDrillIn={(filter) => { setDrillFilter(filter); setActiveTab('orders'); }}
                onEditOrder={(orderId) => { setPendingEditOrderId(orderId); setPlanningReturnContext(null); setActiveTab('orders'); }}
              />
            </div>
          )}
          {mountedTabs.has('products') && (
            <div style={{ display: activeTab === 'products' ? 'block' : 'none' }}>
              <ProductsTab key={`products-${tabNonces.products || 0}`} products={products} fetchData={fetchProducts} dialogOpen={dialogOpen} setDialogOpen={setDialogOpen} />
            </div>
          )}
          {/* Orders intentionally NOT kept mounted — fresh remount on every visit so
              filters / sort / search / drill / dealer pickers reset to defaults. */}
          {activeTab === 'orders' && (
            <OrdersTab key={`orders-${ordersVisitKey}-${tabNonces.orders || 0}`} orders={orders} products={products} dealers={dealers} transporters={transporters} crmOutstandingByDealerId={crmOutstandingByDealerId} freightByOrderId={freightByOrderId} fetchData={fetchOrders} patchOrder={patchOrder} removeOrder={removeOrder} getStatusBadge={getStatusBadge} orderDialogOpen={orderDialogOpen} setOrderDialogOpen={setOrderDialogOpen} drillFilter={drillFilter} clearDrillFilter={() => setDrillFilter(null)} userRole={user?.role} onNewOrder={() => setActiveTab('new-order')} pendingEditOrderId={pendingEditOrderId} clearPendingEditOrderId={() => setPendingEditOrderId(null)} onPendingEditDialogClosed={handlePendingEditDialogClosed} hideTabBody={!!planningReturnContext} justCreatedOrderIds={justCreatedOrderIds} />
          )}
          {mountedTabs.has('new-order') && (
            <div style={{ display: activeTab === 'new-order' ? 'block' : 'none' }}>
              <NewOrderTab key={`new-order-${tabNonces['new-order'] || 0}`} dealers={dealers} products={products} orders={orders} fetchData={fetchOrders} patchOrder={patchOrder} onOrderCreated={() => setActiveTab('orders')} />
            </div>
          )}
          {mountedTabs.has('dealers') && (
            <div style={{ display: activeTab === 'dealers' ? 'block' : 'none' }}>
              <DealersTab key={`dealers-${tabNonces.dealers || 0}`} dealers={dealers} fetchData={fetchDealers} dealerDialogOpen={dealerDialogOpen} setDealerDialogOpen={setDealerDialogOpen} userRole={user?.role} onNavigate={(filter) => { setDrillFilter(filter); setActiveTab('orders'); }} />
            </div>
          )}
          {mountedTabs.has('dealer-crm') && (
            <div style={{ display: activeTab === 'dealer-crm' ? 'block' : 'none' }}>
              <DealerCRMTab key="dealer-crm-persistent" refreshKey={tabNonces['dealer-crm'] || 0} userRole={user?.role} products={products} />
            </div>
          )}
          {mountedTabs.has('reports') && (
            <div style={{ display: activeTab === 'reports' ? 'block' : 'none' }}>
              {/* Reports = locked Dispatch Details view (status forced to dispatched, Clear All hidden). */}
              <ReportsTab key={`reports-${tabNonces.reports || 0}`} orders={orders} products={products} dealers={dealers} transporters={transporters} crmOutstandingByDealerId={crmOutstandingByDealerId} fetchData={fetchOrders} getStatusBadge={getStatusBadge} userRole={user?.role} onNewOrder={() => setActiveTab('new-order')} />
            </div>
          )}
          {mountedTabs.has('product-summary') && (
            <div style={{ display: activeTab === 'product-summary' ? 'block' : 'none' }}>
              <ProductSummaryTab key="product-summary-persistent" refreshKey={tabNonces['product-summary'] || 0} orders={orders} onNavigate={(filter) => { setDrillFilter(filter); setActiveTab('orders'); }} />
            </div>
          )}
          {mountedTabs.has('transport') && (
            <div style={{ display: activeTab === 'transport' ? 'block' : 'none' }}>
              <TransportTab key={`transport-${tabNonces.transport || 0}`} transporters={transporters} fetchData={fetchTransporters} userRole={user?.role} />
            </div>
          )}
          {mountedTabs.has('stock') && (
            <div style={{ display: activeTab === 'stock' ? 'block' : 'none' }}>
              <StockTab key={`stock-${tabNonces.stock || 0}`} refreshKey={tabNonces.stock || 0} products={products} orders={orders} transporters={transporters} fetchData={fetchOrders} userRole={user?.role} onNavigateToProduction={(sheetId) => { setDrillFilter({ focus_sheet_id: sheetId }); setActiveTab('production'); }} />
            </div>
          )}
          {mountedTabs.has('production') && (
            <div style={{ display: activeTab === 'production' ? 'block' : 'none' }}>
              <ProductionTab key="production-persistent" refreshKey={tabNonces.production || 0} products={products} drillFilter={drillFilter} clearDrillFilter={() => setDrillFilter(null)} />
            </div>
          )}
          {mountedTabs.has('planning') && (
            <div style={{ display: activeTab === 'planning' ? 'block' : 'none' }}>
              <PlanningTab key="planning-persistent" refreshKey={tabNonces.planning || 0} orders={orders} products={products} onEditOrder={triggerEditOrderFromPlanning} restoreDrillContext={planningRestoreArmed ? planningReturnContext : null} onRestoreDrillContextConsumed={() => { setPlanningRestoreArmed(false); setPlanningReturnContext(null); }} />
            </div>
          )}
          {mountedTabs.has('users') && (
            <div style={{ display: activeTab === 'users' ? 'block' : 'none' }}>
              <UsersTab key={`users-${tabNonces.users || 0}`} />
            </div>
          )}
          {mountedTabs.has('costing') && user?.role === 'admin' && (
            <div style={{ display: activeTab === 'costing' ? 'block' : 'none' }}>
              <ProductCostingTab key={`costing-${tabNonces.costing || 0}`} refreshKey={tabNonces.costing || 0} products={products} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Transporter management tab
